import { clsx } from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { easeOutExpo, useReducedMotion } from './motion';

/**
 * Counts up (or down) to `value` with an ease-out-expo curve on requestAnimationFrame. Tabular
 * figures so the width holds. Jumps straight to the value under reduced motion. Screen readers
 * get only the final value.
 *
 *   <NumberTicker value={ovr} from={40} duration={1200} className="tts-display text-6xl" />
 *   <NumberTicker value={0.483} decimals={1} format={(n) => `${n}%`} />
 */
export interface NumberTickerProps {
  value: number;
  /** Start of the first run. Later runs start from what is on screen. Default: value (no tick). */
  from?: number;
  duration?: number;
  decimals?: number;
  format?: (n: number) => string;
  className?: string;
}

/** The number on screen `elapsed` ms into a tick from `from` to `to`. Pure, for tests. */
export function tickerValueAt(from: number, to: number, elapsed: number, duration: number, decimals = 0): number {
  const t = duration <= 0 ? 1 : elapsed / duration;
  const raw = from + (to - from) * easeOutExpo(t);
  const f = 10 ** decimals;
  const v = Math.round(raw * f) / f;
  // Never overshoot the target, whatever the rounding does.
  return to >= from ? Math.min(v, to) : Math.max(v, to);
}

export function NumberTicker({ value, from, duration = 900, decimals = 0, format, className }: NumberTickerProps) {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(() => (reduced ? value : (from ?? value)));
  const shownRef = useRef(shown);
  shownRef.current = shown;

  useEffect(() => {
    const start = shownRef.current;
    if (reduced || start === value || duration <= 0) {
      setShown(value);
      return;
    }
    let raf = 0;
    let t0: number | null = null;
    const frame = (ts: number) => {
      if (t0 === null) t0 = ts;
      const elapsed = ts - t0;
      const next = tickerValueAt(start, value, elapsed, duration, decimals);
      setShown(next);
      if (elapsed < duration) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, decimals, reduced]);

  const fmt = (n: number) => (format ? format(n) : n.toFixed(decimals));
  return (
    <span className={clsx('tabular-nums', className)}>
      <span aria-hidden="true">{fmt(shown)}</span>
      <span className="sr-only">{fmt(value)}</span>
    </span>
  );
}
